import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowLeftIcon,
  EditIcon,
  MapPin,
  Phone,
  Plus,
  Receipt,
  SplitVertical,
  UserIcon,
} from "@/components/icons";
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { displayCustomerAddress } from "@/modules/customers/domain/address";
import { ProductFormSectionLike } from "@/modules/orders/presentation/ui/OrderSection";
import { getOrderStatusMeta } from "@/modules/orders/presentation/ui/order-status";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  useCustomerOrdersQuery,
  useCustomerProductAffinityQuery,
  useCustomerQuery,
  useCustomerRiskQuery,
} from "../api/queries";
import { CustomerEditDialog } from "../ui/CustomerEditDialog";
import { CustomerProductAffinityCard } from "../ui/CustomerProductAffinityCard";
import { CustomerRiskCard } from "../ui/CustomerRiskCard";
import { CustomerWaitlistSection } from "../ui/CustomerWaitlistSection";

const PAGE_SIZE = 10;

function HeroStat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 px-4 py-3 sm:px-5">
      <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <span className="truncate font-display text-xl tabular-nums leading-none sm:text-2xl">
        {value}
      </span>
      {hint ? (
        <span className="truncate text-muted-foreground text-xs">{hint}</span>
      ) : null}
    </div>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams({ from: "/app/customers/$id" });
  const nav = useNavigate();
  const canWrite = useActionPermission("orders:write");
  const [offset, setOffset] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const profile = useCustomerQuery(id);
  const risk = useCustomerRiskQuery(id);
  const orders = useCustomerOrdersQuery(id, { limit: PAGE_SIZE, offset });
  const affinity = useCustomerProductAffinityQuery(
    id,
    { limit: 8 },
    { enabled: (profile.data?.stats.confirmedOrderCount ?? 0) > 0 },
  );

  if (profile.isLoading) {
    return (
      <>
        <Header />
        <Main className="mx-auto flex w-full max-w-4xl flex-col gap-6">
          <Skeleton className="h-44 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </Main>
      </>
    );
  }

  if (!profile.data) {
    return (
      <>
        <Header />
        <Main className="mx-auto w-full max-w-4xl">
          <div className="brand-card p-8">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>ບໍ່ພົບລູກຄ້າ</EmptyTitle>
                <EmptyDescription>
                  ລູກຄ້າອາດຖືກລຶບ ຫຼື ID ບໍ່ຖືກຕ້ອງ
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        </Main>
      </>
    );
  }

  const c = profile.data;
  const stats = c.stats;
  const orderRows = orders.data?.data ?? [];
  const totalOrders = orders.data?.meta.total ?? 0;
  const customerAddress = displayCustomerAddress(c);

  return (
    <>
      <Header />
      <Main className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="mb-1 w-fit rounded-full px-0 text-muted-foreground hover:text-foreground"
              onClick={() => nav({ to: "/app/customers" })}
            >
              <ArrowLeftIcon data-icon="inline-start" />
              ກັບລາຍຊື່ລູກຄ້າ
            </Button>
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ໂປຣໄຟລ໌ລູກຄ້າ
            </h2>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>

          {canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="rounded-full border-secondary/40"
                onClick={() => setEditOpen(true)}
              >
                <EditIcon data-icon="inline-start" />
                ແກ້ໄຂ
              </Button>
              <Button
                variant="outline"
                className="rounded-full border-secondary/40"
                onClick={() =>
                  nav({
                    to: "/app/customers/merge",
                    search: { sourceId: c.id },
                  })
                }
              >
                <SplitVertical data-icon="inline-start" />
                Merge
              </Button>
              <Button
                className="rounded-full"
                onClick={() =>
                  nav({
                    to: "/app/orders/new",
                    search: { customerId: c.id },
                  })
                }
              >
                <Plus data-icon="inline-start" />
                ສ້າງອໍເດີ້
              </Button>
            </div>
          ) : null}
        </div>

        <section className="brand-card overflow-hidden p-0">
          <div className="bg-linear-to-br from-secondary/12 via-primary/6 to-background px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-background/80 text-secondary shadow-sm">
                  <UserIcon className="size-6" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-2xl">{c.name}</h3>
                    {stats.isReturning ? (
                      <Badge className="rounded-full border-leaf/30 bg-leaf/10 text-leaf">
                        ຊື້ຊ້ຳ
                      </Badge>
                    ) : stats.orderCount > 0 ? (
                      <Badge variant="secondary" className="rounded-full">
                        ລູກຄ້າໃໝ່
                      </Badge>
                    ) : null}
                  </div>
                  {c.phone ? (
                    <p className="mt-1 flex items-center gap-2 text-muted-foreground text-sm">
                      <Phone className="size-4 shrink-0" />
                      {c.phone}
                    </p>
                  ) : null}
                  {customerAddress ? (
                    <p className="mt-2 flex items-start gap-2 text-muted-foreground text-sm">
                      <MapPin className="mt-0.5 size-4 shrink-0" />
                      <span className="whitespace-pre-line">{customerAddress}</span>
                    </p>
                  ) : (
                    <p className="mt-2 text-muted-foreground text-sm">
                      ຍັງບໍ່ມີທີ່ຢູ່
                    </p>
                  )}
                  {c.note ? (
                    <p className="mt-3 rounded-xl bg-background/60 px-3 py-2 text-sm">
                      <span className="font-medium">ໝາຍເຫດ: </span>
                      {c.note}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 border-secondary/10 border-t bg-muted/15 lg:grid-cols-4">
            <HeroStat
              label="ອໍເດີ້"
              value={String(stats.orderCount)}
            />
            <HeroStat
              label="ຢືນຢັນ"
              value={String(stats.confirmedOrderCount)}
            />
            <HeroStat
              label="ຍອດລວມ"
              value={formatLak(stats.totalRevenue)}
            />
            <HeroStat
              label="ລ່າສຸດ"
              value={
                stats.lastOrderAt
                  ? formatDateTimeLocal(stats.lastOrderAt)
                  : "—"
              }
              hint={
                stats.firstOrderAt
                  ? `ຄັ້ງທຳອິດ ${formatDateTimeLocal(stats.firstOrderAt)}`
                  : undefined
              }
            />
          </div>
        </section>

        <CustomerWaitlistSection customerId={c.id} customerName={c.name} />

        <CustomerRiskCard risk={risk.data} isLoading={risk.isLoading} />

        <CustomerProductAffinityCard
          result={affinity.data}
          isLoading={affinity.isLoading}
        />

        <ProductFormSectionLike
          icon={Receipt}
          title="ປະຫວັດອໍເດີ້"
          description={
            totalOrders > 0 ? `${totalOrders} ລາຍການ` : "ຍັງບໍ່ມີອໍເດີ້"
          }
        >
          {orders.isLoading ? (
            <Skeleton className="h-48 rounded-xl" />
          ) : orderRows.length === 0 ? (
            <div className="rounded-2xl bg-muted/20 px-4 py-8 text-center text-muted-foreground text-sm">
              ຍັງບໍ່ມີອໍເດີ້
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {orderRows.map((o) => {
                const meta = getOrderStatusMeta(o.status);
                return (
                  <Link
                    key={o.id}
                    to="/app/orders/$id"
                    params={{ id: o.id }}
                    className={cn(
                      "flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/50",
                      "bg-background/70 px-4 py-3 transition-colors hover:border-secondary/35 hover:bg-secondary/5",
                    )}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{o.orderNumber}</span>
                        <Badge
                          variant={meta.variant}
                          className="rounded-full text-xs"
                        >
                          {meta.label}
                        </Badge>
                      </div>
                      <p className="mt-1 text-muted-foreground text-xs">
                        {formatDateTimeLocal(o.createdAt)} · {o.itemCount}{" "}
                        ລາຍການ
                      </p>
                    </div>
                    <span className="font-medium tabular-nums">
                      {formatLak(o.total)}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {totalOrders > PAGE_SIZE ? (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={offset === 0}
                onClick={() => setOffset((v) => Math.max(0, v - PAGE_SIZE))}
              >
                ກ່ອນໜ້າ
              </Button>
              <span className="text-muted-foreground text-sm tabular-nums">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, totalOrders)} /{" "}
                {totalOrders}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={offset + PAGE_SIZE >= totalOrders}
                onClick={() => setOffset((v) => v + PAGE_SIZE)}
              >
                ຖັດໄປ
              </Button>
            </div>
          ) : null}
        </ProductFormSectionLike>
      </Main>

      {canWrite ? (
        <CustomerEditDialog
          customer={c}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      ) : null}
    </>
  );
}
